import { useState, useContext, useEffect, useRef } from "react";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";

import SuiBox from "components/SuiBox";
import SuiButton from "components/SuiButton";

import SuiTypography from "components/SuiTypography";
import Masonry, { ResponsiveMasonry } from "react-responsive-masonry";

import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Card from "@mui/material/Card";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Chip from "@mui/material/Chip";

import FullscreenIcon from "@mui/icons-material/Fullscreen";
import DownloadIcon from "@mui/icons-material/Download";
import DeleteIcon from "@mui/icons-material/Delete";
import ScheduleIcon from "@mui/icons-material/Schedule";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";

import { saveAs } from "file-saver";
import Prompt from "components/UpStyled/Prompt";
import moment from "moment";

import ImageCardWrapper from "components/UpStyled/ImageCardWrapper";

import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";

import { Empty } from "antd";
import colors from "assets/theme/base/colors";

import Lightbox from "yet-another-react-lightbox";
import Zoom from "yet-another-react-lightbox/plugins/zoom";
import Download from "yet-another-react-lightbox/plugins/download";
import Thumbnails from "yet-another-react-lightbox/plugins/thumbnails";

const smartdate = require("smartdate");
smartdate.init();

const AIResultView = ({ resultData, setResultData, setIsDirty }) => {
  const [imgsViewerVisible, setImgsViewerVisible] = useState(false);
  const [imgsViewerCurrent, setImgsViewerCurrent] = useState(0);
  const [deleteVisible, setDeleteVisible] = useState(false); //delete the image in dataset
  const [deleteResultVisible, setDeleteResultVisible] = useState(false); //delete dataset
  const [deleteIndex, setDeleteIndex] = useState(null);
  const [selectedResult, setSelectedResult] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [copiedText, setCopiedText] = useState();

  let images = [];

  for (let i = 0; i < resultData.length; i++) {
    let rd_images = resultData[i]?.images || [];
    for (let j = 0; j < rd_images.length; j++) {
      images.push(rd_images[j]);
    }
  }

  const openImgsViewer = () => setImgsViewerVisible(true);
  const closeImgsViewer = () => setImgsViewerVisible(false);

  const handleFullScreen = (e, url) => {
    const index = images.findIndex((x) => x == url);
    if (index > -1) {
      setImgsViewerCurrent(index);
      openImgsViewer();
    }
  };

  const handleDownload = (e, url) => {
    saveAs(url);
  };

  const handleDeleteResult = (rd) => {
    const newresultData = resultData.filter((r) => rd.id !== r.id);
    setResultData(newresultData);
    setIsDirty(true);
  };

  const handleDeleteResultImage = (e, aiResult, index) => {
    const newresultData = [];

    for (let i = 0; i < resultData.length; i++) {
      if (aiResult?.id === resultData[i]?.id) {
        const newAiResult = {
          ...resultData[i],
          images: resultData[i].images.filter((x, inx) => inx !== index),
        };

        //delete the whole result if it doesn't contain any images
        if (newAiResult.images.length > 0) {
          newresultData.push(newAiResult);
        }
      } else {
        newresultData.push(resultData[i]);
      }
    }

    setResultData(newresultData);
    setIsDirty(true);
  };

  return (
    <SuiBox sx={{ maxHeight: 600, overflow: "auto", borderRadius: 2 }}>
      <Backdrop sx={{ color: "info", zIndex: (theme) => 99999 }} open={uploading}>
        <CircularProgress color="info" sx={{ mr: 1 }} size={32} />
        <SuiTypography variant="h6" fontWeight="medium" color="info">
          Processing...
        </SuiTypography>
      </Backdrop>
      <Lightbox
        open={imgsViewerVisible}
        close={closeImgsViewer}
        slides={images.map((x, index) => ({ src: x, id: index }))}
        plugins={[Zoom, Download, Thumbnails]}
        zoom={{ maxZoomPixelRatio: 3 }}
        index={imgsViewerCurrent}
      />

      <SuiBox mb={1} height="100%">
        {resultData.map((result) => {
          const conditional_img = result?.task_meta?.conditional;

          let prompt_text = result?.task_meta?.prompt || "";

          return (
            <div>
              <SuiBox
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: 0.5,
                  px: 1,
                  mb: 0.5,
                  height: 74,
                  backgroundColor: colors.light.main,
                }}
              >
                <SuiBox sx={{ display: "flex", gap: 1, flexGrow: 1 }}>
                  <SuiBox
                    sx={{
                      display: { xs: "none", sm: "none", md: "flex", lg: "flex" },
                      alignItems: "center",
                      gap: 1,
                      minWidth: 120,
                    }}
                  >
                    <ScheduleIcon fontSize="small" />
                    {smartdate.format(new Date(result?.task_meta?.end_at))}
                  </SuiBox>
                  <SuiBox
                    sx={{
                      display: { xs: "flex", sm: "flex", md: "none", lg: "none" },
                      alignItems: "center",
                      gap: 1,
                      minWidth: 20,
                    }}
                  >
                    <Tooltip title={smartdate.format(new Date(result?.task_meta?.end_at))}>
                      <ScheduleIcon fontSize="small" />
                    </Tooltip>
                  </SuiBox>

                  {conditional_img ? (
                    <SuiBox
                      display="flex"
                      gap={0.5}
                      alignItems="center"
                      sx={{ minWidth: 120, overflow: "hidden" }}
                    >
                      <SuiTypography variant="body1" fontWeight="bold" color="dark">
                        Image:
                      </SuiTypography>
                      <div>
                        <ImageCardWrapper
                          data={{ url: conditional_img, type: "image/*" }}
                          alt={"image_conditional"}
                          width={65}
                          height={65}
                          sx={{ cursor: "pointer" }}
                        />
                      </div>
                    </SuiBox>
                  ) : null}

                  <SuiBox display="flex" flexDirection="column" justifyContent="center">
                    {prompt_text ? (
                      <SuiBox display="flex" gap={0.5} alignItems="center">
                        <SuiBox
                          sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 85 }}
                        >
                          <SuiTypography variant="body1" fontWeight="bold" color="dark">
                            Prompts:{" "}
                          </SuiTypography>
                          <div>
                            <a
                              href=""
                              onClick={(e) => {
                                e.preventDefault();
                                navigator.clipboard.writeText(prompt_text);
                                setCopiedText(prompt_text);
                              }}
                            >
                              {copiedText == prompt_text ? (
                                <CheckIcon color="success" fontSize="small" />
                              ) : (
                                <ContentCopyIcon fontSize="small" />
                              )}
                            </a>
                          </div>
                        </SuiBox>
                        <div>
                          <SuiBox sx={{ px: 1 }}>
                            <Tooltip title={prompt_text}>
                              <SuiTypography
                                variant="body1"
                                color="text"
                                sx={{
                                  fontSize: { xs: 12, sm: 12, md: 12, lg: 14 },
                                  lineHeight: 1.2,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  display: "-webkit-box",
                                  WebkitLineClamp: "2",
                                  WebkitBoxOrient: "vertical",
                                }}
                              >
                                {prompt_text}
                              </SuiTypography>
                            </Tooltip>
                          </SuiBox>
                        </div>
                      </SuiBox>
                    ) : null}
                  </SuiBox>
                </SuiBox>
                <SuiBox
                  display="flex"
                  flexDirection="column"
                  justifyContent="space-between"
                  gap={0.5}
                  sx={{ minWidth: 100 }}
                >
                  <Tooltip title="Keep working on this result.">
                    <SuiButton
                      color="error"
                      variant="outlined"
                      size="small"
                      onClick={() => {
                        setSelectedResult(result);
                        setDeleteResultVisible(true);
                      }}
                      sx={{
                        padding: "0px 8px",
                        fontSize: 12,
                        lineHeight: 0,
                        minHeight: 30,
                        textTransform: "capitalize",
                        borderRadius: "4px",
                      }}
                    >
                      Delete
                    </SuiButton>
                  </Tooltip>
                </SuiBox>
              </SuiBox>

              <ResponsiveMasonry
                columnsCountBreakPoints={{ 250: 1, 650: 2, 900: 3, 1300: 4, 1600: 5 }}
              >
                <Masonry>
                  {result.images.map((url, index) => {
                    return (
                      <SuiBox sx={{ mb: 1, mx: 1 }}>
                        <div
                          className="hover-parent"
                          style={{
                            position: "relative",
                            minHeight: 200,
                            borderRadius: 12,
                            overflow: "hidden",
                          }}
                        >
                          <img
                            src={url}
                            alt={"image_" + index}
                            style={{ width: "100%", height: "auto", cursor: "pointer" }}
                            onClick={(e) => handleFullScreen(e, url)}
                          />

                          <SuiBox
                            className="hover-children"
                            sx={{ position: "absolute", top: 4, right: 4, left: 4, width: "auto" }}
                          >
                            <SuiBox
                              display="flex"
                              alignItems="center"
                              justifyContent="flex-end"
                              gap={1}
                            >
                              <Tooltip title={"Delete"}>
                                <IconButton
                                  size="small"
                                  color="dark"
                                  sx={{
                                    backgroundColor: "#ffffff",
                                    "&:hover": {
                                      backgroundColor: "#e3e3e3",
                                    },
                                  }}
                                  onClick={(e) => {
                                    setDeleteVisible(true);
                                    setDeleteIndex(index);
                                    setSelectedResult(result);
                                  }}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Tooltip>

                              <Tooltip title={"Fullscreen"}>
                                <IconButton
                                  size="small"
                                  color="dark"
                                  sx={{
                                    backgroundColor: "#ffffff",
                                    "&:hover": {
                                      backgroundColor: "#e3e3e3",
                                    },
                                  }}
                                  onClick={(e) => handleFullScreen(e, url)}
                                >
                                  <FullscreenIcon />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title={"Download"}>
                                <IconButton
                                  size="small"
                                  color="dark"
                                  sx={{
                                    backgroundColor: "#ffffff",
                                    "&:hover": {
                                      backgroundColor: "#e3e3e3",
                                    },
                                  }}
                                  onClick={(e) => handleDownload(e, url)}
                                >
                                  <DownloadIcon />
                                </IconButton>
                              </Tooltip>
                            </SuiBox>
                          </SuiBox>
                        </div>
                      </SuiBox>
                    );
                  })}
                </Masonry>
              </ResponsiveMasonry>
            </div>
          );
        })}
        {resultData.length == 0 ? (
          <SuiBox height="100%">
            {" "}
            <Empty />{" "}
          </SuiBox>
        ) : null}
      </SuiBox>

      {deleteResultVisible ? (
        <Prompt
          heading="Delete Confirmation"
          text={`Are you certain you want to delete this result?`}
          visible={deleteResultVisible}
          okText="Delete"
          okColor="error"
          handleOk={() => {
            handleDeleteResult(selectedResult);
            setDeleteResultVisible(false);
          }}
          handleClose={() => setDeleteResultVisible(false)}
        />
      ) : null}
      {deleteVisible ? (
        <Prompt
          heading="Delete Confirmation"
          text={`Are you certain you want to delete this result image?`}
          visible={deleteVisible}
          okText="Delete"
          okColor="error"
          handleOk={(e) => {
            handleDeleteResultImage(e, selectedResult, deleteIndex);
            setDeleteVisible(false);
          }}
          handleClose={() => setDeleteVisible(false)}
        />
      ) : null}
    </SuiBox>
  );
};

export default AIResultView;
